import { CHURN_REASON, type ChurnSurveyBody } from '@nx/state';
import { validateYup } from '@nx/stdlib';
import * as yup from 'yup';

export interface ChurnSurveyData {
  reasons: { [key in CHURN_REASON]: boolean };
  comment?: string;
}

export const getChurnSurveyDefaults = (): ChurnSurveyData => ({
  reasons: {
    [CHURN_REASON.EXPENSIVE]: false,
    [CHURN_REASON.DIFFICULT]: false,
    [CHURN_REASON.FEATURES]: false,
    [CHURN_REASON.PERFORMANCE]: false,
    [CHURN_REASON.FOUND_ALTERNATIVE]: false,
    [CHURN_REASON.DOCUMENTATION]: false,
    [CHURN_REASON.OTHER]: false,
  },
  comment: undefined,
});

export function shuffle<T>(array: readonly T[]): T[] {
  const shuffled: T[] = [...array];

  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const tempI = shuffled[i];
    const tempJ = shuffled[j];
    if (tempI === undefined || tempJ === undefined) {
      throw new Error('Array contains unexpected gaps, cannot shuffle');
    }
    const temp = tempI;
    shuffled[i] = tempJ;
    shuffled[j] = temp;
  }

  return shuffled;
}

export const transformChurnSurveyData = (data: ChurnSurveyData): Omit<ChurnSurveyBody, 'dbid'> => ({
  reasons: Object.entries(data.reasons).reduce((acc: string[], [key, value]) => {
    if (value) {
      acc.push(key);
    }
    return acc;
  }, []),
  comment: (data.comment?.trim() ?? '') || null,
});

const schema = yup.object({
  reasons: yup
    .object({
      [CHURN_REASON.EXPENSIVE]: yup.boolean(),
      [CHURN_REASON.DIFFICULT]: yup.boolean(),
      [CHURN_REASON.FEATURES]: yup.boolean(),
      [CHURN_REASON.PERFORMANCE]: yup.boolean(),
      [CHURN_REASON.FOUND_ALTERNATIVE]: yup.boolean(),
      [CHURN_REASON.DOCUMENTATION]: yup.boolean(),
      [CHURN_REASON.OTHER]: yup.boolean(),
    })
    .test('at-least-one-reason', 'At least one reason must be selected', (value) => Object.values(value).some((v) => v))
    .label('Churn reasons'),
  comment: yup
    .string()
    .trim()
    .max(1500)
    .optional()
    .label('Additional comments')
    .test(
      'comment-given-if-reason-other',
      'A comment must be provided if "other" is selected as a reason.',
      (value, { parent }) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        const isOtherSelected = parent.reasons[CHURN_REASON.OTHER] === true;
        return isOtherSelected ? Boolean(value) : true;
      },
    ),
});

export const validateChurnSurveyData = (data: ChurnSurveyData) => validateYup(schema, data);
